import moment from "moment";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MyDocumentsType } from "../../types/main";
import { FlexCol, FlexRow } from "../../utils/flexLayouts";
import { renderHighlightedText } from "../../utils/searchHighlight";
import { EmptyList } from "../EmptyList";
import Loading from "../Loading";
import DocumentSnippetIconTooltip from "../ui-icons/table/DocumentSnippetIconTooltip.mobile";
import SortIcon from "../ui-icons/table/SortIcon";
import { CriticallyListItem, TableHeaderColumnProps } from "../ui/table/Table";
import { UserAvatarItem, UserAvatarItemStore } from "../ui/TGUserAvatarsList";
import { useAppSelector } from "src/hooks/redux";
import { getUsers } from "src/modules/users/actions";
import { selectUsers } from "src/modules/users/selector";
import { getNameAndSurname } from "src/utils/users.utils";
import { User } from "src/modules/users/types";

const tableHeaderContent = {
  code: {
    title: "Код",
    sort: true,
    style: { width: "18%", position: "sticky" as const, left: 0, backgroundColor: "white" },
  },
  aboutDocument: {
    title: "О документе",
    sort: false,
    style: { width: "40%" },
  },
  responsibleForRevision: {
    title: "Ответственный за пересмотр",
    sort: false,
    style: { width: "16%" },
  },
  publicationDate: {
    title: "Дата утверждения",
    sort: true,
    style: { width: "13%" },
  },
  critical: {
    title: "Критичность",
    sort: false,
    style: { width: "13%" },
  },
};

const TableWithFilters = ({ documents, cb, loading }: { documents: MyDocumentsType[]; cb: any; loading: boolean }) => {
  const limit = documents.length;
  // const [page, setPage] = useState(1);

  // const { numberOfPages, pageItems, length: favoriteItemsCount } = usePagination(documents, limit, page);

  // function changePageHandler(page: number) {
  //   setPage(page);
  // }

  return (
    <Table
      tableName="Table with filters"
      header={tableHeaderContent}
      body={documents}
      limit={limit}
      cb={cb}
      loading={loading}
      // count={favoriteItemsCount}
      // numberOfPages={numberOfPages}
      // page={page}
      // onChangePage={changePageHandler}
    />
  );
};

export default TableWithFilters;

export type CodeType = { code: string; name: string };

export type TableBodyContentItem = {
  id: number;
  code: CodeType;
  aboutDocument: {
    collection: string;
    department: string;
    sector: string;
    process: string;
    type: string;
  };
  responsibleForRevision: {
    name: string;
    avatar: string | null;
    email: string;
    shortName: string;
  };
  publicationDate: string;
  critical: boolean;
};

function Table({
  header,
  body,
  tableName,
  limit,
  cb,
  loading,
}: // count,
// numberOfPages,
// onChangePage,
// page,
{
  header: typeof tableHeaderContent;
  body: TableBodyContentItem[];
  limit: number;
  tableName: string;
  cb: any;
  loading: boolean;
  // count: number;
  // page: number;
  // numberOfPages: number;
  // onChangePage: (page: number) => void;
}) {
  const [sortedField, setSortedField] = useState<keyof typeof tableHeaderContent | null>("publicationDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "default">("desc");

  const handleSort = (field: keyof typeof tableHeaderContent) => {
    if (sortedField === field) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : prevDirection === "desc" ? "default" : "asc"
      );
    } else {
      setSortedField(field);
      setSortDirection("asc");
    }
  };

  const sortedTableBodyContent = useMemo(() => {
    if (!sortedField || sortDirection === "default") {
      return body;
    }

    const sortedData = [...body];

    sortedData.sort((a, b) => {
      if (sortedField === "code") {
        return sortDirection === "asc"
          ? a[sortedField].code.localeCompare(b[sortedField].code)
          : b[sortedField].code.localeCompare(a[sortedField].code);
      }

      const valueA = a[sortedField];
      const valueB = b[sortedField];

      if (sortedField === "publicationDate") {
        return sortDirection === "asc"
          ? moment(a[sortedField], "DD.MM.YYYY").diff(moment(b[sortedField], "DD.MM.YYYY"))
          : moment(b[sortedField], "DD.MM.YYYY").diff(moment(a[sortedField], "DD.MM.YYYY"));
      }

      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortDirection === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
      } else if (typeof valueA === "boolean" && typeof valueB === "boolean") {
        return sortDirection === "asc" ? Number(valueA) - Number(valueB) : Number(valueB) - Number(valueA);
      }

      return 0;
    });

    return sortedData;
  }, [sortedField, sortDirection, body]) as TableBodyContentItem[];

  return (
    <TableList
      head={header}
      body={sortedTableBodyContent}
      sort={handleSort}
      sortDirection={sortDirection}
      sortedField={sortedField}
      cb={cb}
      loading={loading}
    />
  );
}

function TableList({
  head,
  body,
  sort,
  sortDirection,
  sortedField,
  cb,
  loading,
}: {
  head: typeof tableHeaderContent;
  body: MyDocumentsType[];
  sort: (f: keyof typeof tableHeaderContent) => void;
  sortedField: keyof typeof tableHeaderContent | null;
  sortDirection: "asc" | "desc" | "default";
  cb: any;
  loading: boolean;
}) {
  const tableFields = useMemo(() => {
    return Object.keys(head) as (keyof typeof tableHeaderContent)[];
  }, [head]);

  // useMore(() => cb(), [body]);

  return (
    <div
      id="table-scroll"
      className="w-auto pb-5 pt-0 bg-white rounded-lg table-custom shadow-custom overflow-x-auto scrollbar-none scrollbar-h-2 scrollbar-thumb-[#D1D5DB] max-h-[calc(100vh-170px)] mb-4 tablet:mb-0"
    >
      <FlexRow className="w-full">
        <FlexCol className="relative grow">
          <FlexRow className="">
            <FlexCol className="divide-y grow">
              {Array.isArray(body) && body.length > 0 ? (
                <div className="table-wrap">
                  {body.map((item, i) => (
                    <ListItem item={item} key={item.id} />
                  ))}
                </div>
              ) : loading ? (
                <div className="pt-4">
                  <Loading />
                </div>
              ) : (
                <EmptyList title="Документы не найдены" />
              )}
            </FlexCol>
          </FlexRow>
        </FlexCol>
      </FlexRow>
    </div>
  );
}

function getUserNameWithPersonalNumber(user: User, defaultName: string) {
  return (
    getNameAndSurname(user.DisplayName, defaultName) +
    ` (${
      user.UserProfileProperties.find((u: any) => u.Key === "abiGlobalID").Value ||
      user.UserProfileProperties.find((u: any) => u.Key === "EmployeeNumber").Value ||
      "Нет номера"
    })`
  );
}

function ListItem({ item }: { item: MyDocumentsType }) {
  const [query, setQuery] = useSearchParams();
  const users = useAppSelector((state) => state.users.items);

  function openDocumentCard(id: number) {
    const newSearchParams = new URLSearchParams(query);

    newSearchParams.set("documentCardMobile", id.toString());

    setQuery(newSearchParams);
  }

  const user = users[item.responsibleForRevision.email];

  const userDisplayName =
    user && !!user.UserProfileProperties.length
      ? getUserNameWithPersonalNumber(user, item.responsibleForRevision.email)
      : user?.DisplayName || "";

  return (
    <FlexCol
      className="group gap-x-4 hover:bg-[#F3F4F6] hover:bg-opacity-50 cursor-pointer [&>div]:py-1 [&>div]:px-4 border-b border-[#D1D5DB] py-4"
      key={item.id}
      onClick={() => openDocumentCard(item.id)}
    >
      <FlexCol
        className="break-words bg-white group-hover:bg-[#F9F9FA]"
        style={{ width: "100%", position: "sticky" as const, left: 0 }}
      >
        <FlexRow className="gap-[4px]">
          <CriticallyListItem item={item.critical} isShowText={false}/>
          <div className="text-[13px]">{renderHighlightedText(item.code.code)}</div>
          <div className="ml-auto">
            <DocumentSnippetIconTooltip 
                highlightedText={item.searchMeta?.highlightedText}
                foundBy={item.searchMeta?.foundBy}
            />
          </div>
        </FlexRow>
        <div className="text-darkblue text-[13px] mt-[4px]">{renderHighlightedText(item.code.name)}</div>
      </FlexCol>
      <FlexCol className="break-words text-[#637381]" style={{ width: "100%" }}>

        <FlexRow className="text-[13px]">
          {item.aboutDocument.collection}/{item.aboutDocument.department}/{item.aboutDocument.process}/{item.aboutDocument.sector}/{item.aboutDocument.type}
        </FlexRow>

      </FlexCol>
      <FlexCol className="break-words text-[#637381] text-[13px]" style={{ width: "100%" }}>
        <FlexRow className="space-x-[5px] items-center gap-1">
          Ответственный:
          <FlexCol className="w-6">
            <UserAvatarItemStore item={item.responsibleForRevision} classNameText="hidden" single={true}/>
          </FlexCol>
        </FlexRow>
      </FlexCol>
      <FlexCol className="break-words text-[#637381] text-[13px]" style={{ width: "100%" }}>
        <span>{item.publicationDate}</span>
      </FlexCol>
    </FlexCol>
  );
}

export function TableHeaderColumn({ tableHeaderContent, sortDirection, onClick }: TableHeaderColumnProps) {
  return (
    <FlexCol className="select-none py-2.5 px-4 justify-center" style={tableHeaderContent.style}>
      <FlexRow className="gap-x-2 items-center">
        {tableHeaderContent.icon && <FlexCol>{tableHeaderContent.icon}</FlexCol>}
        <FlexCol className="font-medium whitespace-pre-line w-fit">{tableHeaderContent.title}</FlexCol>
        {tableHeaderContent.sort && (
          <FlexCol className="w-auto cursor-pointer" onClick={onClick}>
            <SortIcon sortDirection={sortDirection} />
          </FlexCol>
        )}
      </FlexRow>
    </FlexCol>
  );
}
